import PropTypes from "prop-types";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  LoadingContext,
  M_StateActions,
  MTable,
  NavigateContext,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Shipper CRUD page.
 */
const SHIPPER_CRUD_ROUTE = "/home/shippers/shipperConfigCrud";

//endregion

//region Component

/**
 * Renders the Shippers configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the Shippers MTable;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - persist the selected shipper before opening View/Edit tabs;
 * - keep the current application tab title synchronized with the active language.
 *
 * Important:
 * - this page is not rendered as an inner tabs page;
 * - therefore it does not use the config-page--tabs modifier;
 * - it also does not pass tabsRailWidth to MTable.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Shippers configuration page.
 */
export default function ShipperConfigPage({
  roles,
  onCreateTab,
  toastMessages,
}) {
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [refresh, setRefresh] = useState(false);

  const selectedTab = useSelector(
    (state) =>
      state.StateKeyValue.find((obj) => obj.key === "selectedTab")?.value
  );

  /**
   * Removes table actions that are not required in this configuration page.
   *
   * Important:
   * - Print is not part of the desired Shippers workflow;
   * - Copy is disabled to keep shipper creation explicit.
   */
  const removeContextItems = useMemo(
    () => ["Print", "Copy"],
    []
  );

  /**
   * Keeps the current application tab label aligned with the active language.
   *
   * Important:
   * - the key matches the backend MenuDisplay value for this page;
   * - this only updates the outer application tab title, not an inner tabs layout.
   */
  useEffect(() => {
    if (!selectedTab) return;

    renameTab(String(selectedTab), t("MenuShippersManagementLabel"));
  }, [renameTab, selectedTab, t]);

  /**
   * Opens the Shipper CRUD tab for the requested operation.
   *
   * Flow:
   * - persists the operation in M_State;
   * - validates row selection for View/Edit/Copy;
   * - persists the selected shipper row when required;
   * - opens the configured CRUD route with a contextual tab label.
   *
   * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
   * @param {object|null|undefined} row - Selected shipper row.
   * @returns {void}
   */
  const openShipperCrud = useCallback(
    (operation, row) => {
      dispatch(
        M_StateActions.addM_State({
          key: "operation",
          value: operation,
        })
      );

      if (["Edit", "Copy", "View"].includes(operation)) {
        if (!row) {
          toastMessages("error", "", t("SelectedLine"));
          return;
        }

        dispatch(
          M_StateActions.addM_State({
            key: "ShipperLine",
            value: row,
          })
        );
      }

      const safeName = row?.Name ?? "";

      const tabLabel =
        operation === "Create"
          ? t("LabelNewShipper")
          : operation === "View"
            ? `${t("LabelViewShipper")} "${safeName}"`
            : `${t("LabelEditShipper")} "${safeName}"`;

      onCreateTab(SHIPPER_CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  /**
   * Replaces MTable default CRUD handlers with the tab-based Shipper workflow.
   */
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ["View", (row) => openShipperCrud("View", row)],
        ["Create", () => openShipperCrud("Create")],
        ["Edit", (row) => openShipperCrud("Edit", row)],
      ]),
    [openShipperCrud]
  );

  return (
    <div
      className="config-page"
      style={{ "--config-page-title-color": ctx.secondaryColor }}
    >
      <div className="config-page__header">
        <div className="config-page__title">
          {t("Shippers")}
        </div>

        <div className="config-page__description">
          {t("InfoShippers")}
        </div>
      </div>

      <div className="config-page__table">
        <MTable
          filtersOnTable
          entityName="Shippers"
          roleName="Shippers"
          rolePermissions={roles?.map((role) => role.Value)}
          removeContextItems={removeContextItems}
          refresh={refresh}
          setRefresh={setRefresh}
          useSessionStorage={false}
          replaceFunctions={replaceFunctions}
        />
      </div>
    </div>
  );
}

//endregion

//region PropTypes

ShipperConfigPage.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};

//endregion